<template>
    <div class="gmf-subheader" v-if="showNotReviewed && showNotReviewedTitle">{{ t('unreviewed_products.title') }}</div>
    <div class="gmf-ordered-product-list-list" v-if="showNotReviewed">
        <a v-for="{ product, hasReview } in cutOffNotReviewedProducts" :key="product.id" :href="product.url" class="gmf-ordered-product-list-list-item" @click="productLinkClick($event, product)">
            <div class="gmf-ordered-product-list-list-item-img"><img :src="product.imageUrl" /></div>
            <div class="gmf-ordered-product-list-list-item-main">
                <div class="gmf-ordered-product-list-list-item-product-name">{{ product.name }}</div>
            </div>
            <div class="gmf-ordered-product-list-list-item-right">
                <button class="gmf-btn gmf-btn-primary gmf-btn-sm" v-if="! hasReview" @click="onReviewClick($event, product)">{{ t('review') }}</button>
            </div>
        </a>
        <a class="gmf-link gmf-text-center" @click="showMoreNotReviewed" v-if="notReviewedProducts.length > notReviewedProductsLimit && ! showAllNotReviews">{{ t('show_more')  }}</a>
        <span class="gmf-text-small" v-if="! notReviewedProducts.length">{{ t('unreviewed_products.no_products') }}</span>
    </div>

    <div class="gmf-subheader gmf-mt-5" v-if="showReviewed && showReviewedTitle">{{ t('reviewed_products.title') }}</div>
    <div class="gmf-ordered-product-list-list" v-if="showReviewed">
        <a v-for="{ product, ratings } in cutOffReviewedProducts" :key="product.id" :href="product.url" class="gmf-ordered-product-list-list-item" @click="productLinkClick($event, product)">
            <div class="gmf-ordered-product-list-list-item-img"><img :src="product.imageUrl" /></div>
            <div class="gmf-ordered-product-list-list-item-main">
                <div class="gmf-ordered-product-list-list-item-product-name">{{ product.name }}</div>
            </div>
            <div class="gmf-ordered-product-list-list-item-right">
                <RatingStars v-if="ratings && ratings.product_score" type="product_score" :rating="ratings.product_score" />
            </div>
        </a>
        <a class="gmf-link gmf-text-center" @click="showMoreReviewed" v-if="reviewedProducts.length > reviewedProductsLimit && ! showAllReviews">{{ t('show_more') }}</a>
        <span class="gmf-text-small" v-if="! reviewedProducts.length">{{ t('reviewed_products.no_products') }}</span>
    </div>

</template>

<style>
.gmf-ordered-product-list-list {
    display: flex;
    flex-direction: column;
    gap: var(--gmf-p-4);
}
.gmf-ordered-product-list-list-item {
    display: flex;
    gap: var(--gmf-p-4);
    cursor: pointer;
    padding: var(--gmf-p-4);
    border-radius: var(--gmf-rounding);
    background: var(--gmf-col-white);
    align-items: center;
}
.gmf-ordered-product-list-list-item:hover {
    background: var(--gmf-col-gray);
}

.gmf-ordered-product-list-list-item-img {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    flex-grow: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.gmf-ordered-product-list-list-item-img img {
    max-width: 100%;
    max-height: 100%;
    border-radius: min(5px, var(--gmf-rounding));
}
.gmf-ordered-product-list-list-item-main {
    flex-grow: 1;
    flex-shrink: 1;
    align-self: center;
    overflow: hidden;
}
.gmf-context .gmf-ordered-product-list-list-item-product-name {
    font-weight: bold;
    text-overflow: ellipsis;
    overflow: hidden;
}
</style>

<script>
import callApi from '@/utils/callApi';
import RatingStars from '@/components/RatingStars';
import state from '@/state';
import openCommentFormV2 from '@/components/CommentFormV2';
import eventbus from '@/utils/eventbus';

export default {
    name: 'OrderedProductList',
    props: {
        products: {
            type: Array,
            required: false,
            default: () => [],
        },

        showReviewed: {
            type: Boolean,
            required: false,
            default: true,
        },

        showNotReviewed: {
            type: Boolean,
            required: false,
            default: true,
        },

        showAllReviewedProducts: {
            type: Boolean,
            required: false,
            default: false,
        },

        showAllNotReviewedProducts: {
            type: Boolean,
            required: false,
            default: false,
        },

        showReviewedTitle: {
            type: Boolean,
            required: false,
            default: true,
        },

        showNotReviewedTitle: {
            type: Boolean,
            required: false,
            default: true,
        },

        closeOnReviewClick: {
            type: Boolean,
            required: false,
            default: false,
        },
        reviewToken: {
            type: String,
            required: false,
            default: null,
        },

        disableProductLink: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data () {
        return {
            reviewedProductsLimit: 4,
            notReviewedProductsLimit: 4,

            orderedProducts: [],

            showAllReviews: this.showAllReviewedProducts,
            showAllNotReviews: this.showAllNotReviewedProducts,
        };
    },
    components: {
        RatingStars,
    },
    async mounted () {
        if (! this.products.length) {
            const res = await callApi('GET', '/v2/client/ordered-products');
            const data = await res.json();
    
            this.orderedProducts = data.orderedProducts;
        } else {
            this.orderedProducts = this.products;
        }
    },
    methods: {
        productLinkClick (evt, product) {
            if (this.disableProductLink) {
                evt.preventDefault();
                evt.stopPropagation();
                this.onReviewClick(evt, product);
            }
        },
        t: state.t.prefix('ordered_products'),
        onReviewClick (evt, product) {
            if (state.merchantConfig.commentForm.useV2CommentForm) {
                if (this.closeOnReviewClick) {
                    eventbus.emit('modal.close');
                }

                openCommentFormV2('product_review', product.id, null, true, { token: this.reviewToken });

                evt.stopPropagation();
                evt.preventDefault();
            }
        },
        showMoreReviewed () {
            this.reviewedProductsLimit += 6;
        },
        showMoreNotReviewed () {
            this.notReviewedProductsLimit += 6;
        },
    },
    computed: {
        reviewedProducts () {
            return this.orderedProducts.filter(({ hasReview, product }) => hasReview && ! product.isNotReviewable);
        },
        notReviewedProducts () {
            return this.orderedProducts.filter(({ hasReview, product }) => ! hasReview && ! product.isNotReviewable);
        },

        cutOffReviewedProducts () {
            if (this.showAllReviews) {
                return this.reviewedProducts;
            }
            return this.reviewedProducts.slice(0, this.reviewedProductsLimit);
        },

        cutOffNotReviewedProducts () {
            if (this.showAllNotReviews) {
                return this.notReviewedProducts;
            }
            return this.notReviewedProducts.slice(0, this.notReviewedProductsLimit);
        },
    },
};
</script>
